import { fmtCompactEur, fmtCompactUsers } from '../formatters.js';
import { MODEL_CHECKPOINTS } from '../modelSnapshot.js';

export default function Hero() {
  const { m12 } = MODEL_CHECKPOINTS;
  const metrics = [
    {
      label: 'M12 total ARR',
      value: fmtCompactEur(m12.totalArr),
      sub: 'B2B ARR plus B2C annualized run-rate',
    },
    {
      label: 'M12 B2C ARR',
      value: fmtCompactEur(m12.b2cArr),
      sub: 'Annualized consumer revenue run-rate',
    },
    {
      label: 'B2C cardholders',
      value: fmtCompactUsers(m12.cardholders),
      sub: 'Month 12 active cardholder target',
    },
  ];

  const principles = [
    [
      '01',
      '12-month plan',
      'The model is anchored on M0 to M12 ARR, not a broad multi-year range.',
    ],
    ['02', 'B2C engine', 'Consumer revenue scales with cardholder ramp and six fee streams.'],
    ['03', 'B2B base', 'Existing B2B ARR starts at $750K and scales to $1.5M by Month 12.'],
    [
      '04',
      'Revenue proof',
      'The Month 12 target is $6.26M ARR, with $4.76M from B2C and $1.5M from B2B.',
    ],
  ];

  return (
    <section className="paper-hero border-b border-carbon-100/80">
      <div className="mx-auto max-w-7xl px-6 py-16 md:py-20">
        <div className="max-w-5xl">
          <p className="caption text-carbon-500">Pulsar Economics / private model</p>
          <h1 className="h1 mt-5 max-w-5xl text-carbon-900">
            12-month revenue plan for the stablecoin neobank.
          </h1>
          <p className="body-lg mt-6 max-w-3xl text-carbon-600">
            A cleaner view of how the current B2B base and the consumer cardholder ramp compound
            into Month 12 ARR.
          </p>
        </div>

        <div className="paper-perspective mt-12">
          <div className="paper-ledger">
            <div className="flex flex-col gap-5 border-b border-carbon-100 p-5 md:flex-row md:items-end md:justify-between md:p-6">
              <div>
                <p className="caption text-carbon-400">Base operating case</p>
                <h2 className="mt-3 text-2xl font-semibold leading-tight text-carbon-900">
                  The model reads from the latest Pulsar Financial sheet.
                </h2>
              </div>
              <div className="inline-flex w-fit items-center gap-2 rounded-sw border border-carbon-200 bg-white px-3 py-2 shadow-sm">
                <span className="label text-carbon-700">M0-M12 plan</span>
              </div>
            </div>

            <div className="grid gap-3 p-5 md:grid-cols-3 md:p-6">
              {metrics.map((metric) => (
                <HeroMetric key={metric.label} {...metric} />
              ))}
            </div>

            <div className="grid gap-3 border-t border-carbon-100 p-5 md:grid-cols-4 md:p-6">
              {principles.map(([num, title, body]) => (
                <div key={title} className="paper-row">
                  <p className="caption text-ice-700 num-tabular">{num}</p>
                  <h3 className="mt-3 text-base font-semibold leading-snug text-carbon-900">
                    {title}
                  </h3>
                  <p className="label mt-2 text-carbon-500">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroMetric({ label, value, sub }) {
  return (
    <div className="paper-metric">
      <p className="caption text-carbon-400">{label}</p>
      <p className="num-tabular mt-4 text-4xl font-semibold leading-none text-carbon-900">
        {value}
      </p>
      <p className="label mt-3 text-carbon-500">{sub}</p>
    </div>
  );
}
